import { jwtDecode } from 'jwt-decode'
import React from 'react'
import { useDispatch } from 'react-redux'

import {
  removeAppConfig,
  setDefaultAgent,
  setRoom,
} from '@store/actionSlices/appConfig'
import {
  removeProjectIdentity,
  setProjectIdentity,
} from '@store/actionSlices/projectIdentity'
import {
  TokenPayloadInterface,
  removeToken,
  setToken,
} from '@store/actionSlices/token'
import { DEFAULT_SHOWCASE_ROOM } from '@store/constants'

import { Auth } from '@api/authentication'

import { notifyError } from '@adUtilities/notifier'

import ResetUtil from './reset-util'

const ISSUER = 'SGC'

interface Props {
  accessToken?: string
}

interface SSOData extends Auth {
  issuer: string
}

function SSO({ accessToken }: Props): void {
  const resetUtil = ResetUtil()

  const dispatch = useDispatch()

  const resetPreviousUserSession = async () => {
    dispatch(removeToken())
    dispatch(removeProjectIdentity())
    dispatch(removeAppConfig())
    resetUtil.resetLocalStorage()
    resetUtil.resetAllApiState()
  }

  const processSSO = async (data: SSOData) => {
    await resetPreviousUserSession()

    if (Object.keys(data).length === 0) {
      notifyError('Invalid token.')
      return
    }

    const projectName = data?.project?.canonical_name || ''
    const projectId = data?.project?.id || ''
    const projectLabel = data?.project?.name || ''
    const user = (data?.user || {}) as TokenPayloadInterface
    const showcaseRoom = data?.showcaseRoom || null
    const issuer = data?.issuer || ''

    if (
      !projectName ||
      !projectId ||
      !projectLabel ||
      Object.keys(user).length === 0 ||
      issuer !== ISSUER
    ) {
      notifyError('Invalid token.')
      return
    }

    dispatch(setDefaultAgent(true))

    dispatch(
      setProjectIdentity({
        projectName,
        projectId,
        projectLabel,
        sessionId: [],
        masterKey: '',
      })
    )

    dispatch(setRoom(showcaseRoom?.name || DEFAULT_SHOWCASE_ROOM))
    dispatch(setToken(user))
  }

  React.useEffect(() => {
    if (!accessToken) {
      return
    }

    try {
      const parsedToken: {
        payload: { data: SSOData }
      } = jwtDecode(accessToken)
      processSSO(parsedToken?.payload?.data || {})
    } catch {
      notifyError('Invalid token.')
    }
  }, [accessToken])
}

export default SSO
